import { supabase } from '@/shared/lib/supabase';

/**
 * Admin-side hosting operations.
 *
 * `enqueueAction` posts to `whm-account-action`, which validates input,
 * resolves the cPanel account, and inserts a `provisioning_job` row.
 * The runner picks it up and runs it within ~1 minute.
 *
 * `getCpanelSsoUrl` calls `whm-cpanel-sso`, which issues a one-time URL
 * from WHM. The caller MUST `window.open(url)` immediately and never
 * persist or log the URL — it is single-use.
 *
 * Every mutation carries an Idempotency-Key (UUID v4). The Edge Function
 * dedupes within a 24h window via the `idempotency_key` table.
 */

// AD-17 (Phase 8c.8): plan changes go through plan-change-quote/confirm, NOT
// through this enqueue path. The legacy 'change_plan' value was removed.
export type WhmAccountAction =
  | 'suspend'
  | 'unsuspend'
  | 'terminate'
  | 'change_password';

export interface EnqueueActionInput {
  /** Slice 3 (G2): required to unsuspend while a `suspended` invoice is still
   *  open and no arrangement exists. The EF answers 409 `open_suspended_invoice`
   *  without it. */
  override_reason?: string;
  action: WhmAccountAction;
  cpanel_username: string;
  reason?: string;
  /** Required for `change_password`. Never logged. */
  new_password?: string;
}

export interface EnqueueActionResponse {
  ok: true;
  provisioning_job_id: string;
  action: WhmAccountAction;
  cpanel_username: string;
}

export interface CpanelSsoResponse {
  ok: true;
  url: string;
  expires_at?: string;
}

interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

async function invokeEdgeFunction<TRes>(
  name: string,
  body: Record<string, unknown>
): Promise<TRes> {
  const idempotencyKey = crypto.randomUUID();
  const { data, error } = await supabase.functions.invoke<TRes | ApiError>(name, {
    body,
    headers: { 'Idempotency-Key': idempotencyKey },
  });
  if (error) {
    const ctx = (error as unknown as { context?: { body?: string } }).context;
    if (ctx?.body) {
      try {
        const parsed = JSON.parse(ctx.body) as ApiError;
        throw new Error(parsed.message ?? error.message);
      } catch {
        // fall through
      }
    }
    throw new Error(error.message);
  }
  if (data && typeof data === 'object' && 'code' in data && 'message' in data) {
    const apiErr = data as ApiError;
    throw new Error(apiErr.message);
  }
  return data as TRes;
}

export type DunningPolicy = 'standard' | 'no_cancel' | 'no_suspend';

export interface SetDunningPolicyResponse {
  ok: true;
  customer_link_id: string;
  cpanel_username: string;
  before: string;
  after: string;
  evaluator_verdict: string | null;
}

export const adminHostingService = {
  /**
   * Slice 3 (2026-09-10, gap F2): change an account's dunning policy through
   * the one sanctioned writer (`customer-link-policy-set`, admin-only,
   * audited). The column itself is service_role-only since Slice 2.
   */
  async setDunningPolicy(input: {
    customer_link_id: string;
    dunning_policy: DunningPolicy;
    reason: string;
  }): Promise<SetDunningPolicyResponse> {
    if (!input.reason.trim()) throw new Error('reason is required');
    return invokeEdgeFunction<SetDunningPolicyResponse>(
      'customer-link-policy-set',
      input as unknown as Record<string, unknown>
    );
  },

  async enqueueAction(input: EnqueueActionInput): Promise<EnqueueActionResponse> {
    if (!input.action) throw new Error('action is required');
    if (!input.cpanel_username) throw new Error('cpanel_username is required');
    if (input.action === 'change_password' && !input.new_password) {
      throw new Error('new_password is required for change_password');
    }
    return invokeEdgeFunction<EnqueueActionResponse>(
      'whm-account-action',
      input as unknown as Record<string, unknown>
    );
  },

  /**
   * Issue a one-time cPanel SSO URL. The caller must open the URL immediately
   * (e.g. `window.open(url, '_blank')`) and NEVER persist it — single-use.
   */
  async getCpanelSsoUrl(cpanelUsername: string): Promise<CpanelSsoResponse> {
    return invokeEdgeFunction<CpanelSsoResponse>('whm-cpanel-sso', {
      cpanel_username: cpanelUsername,
    });
  },
};
